import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'

import { MarketInfo, Reserve, UserPosition } from '@/domain/market-info/marketInfo'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useDebounce } from '@/utils/useDebounce'
import { getCanonicalSymbol } from '@/domain/hooks/useDisplayToken'
import { useEffect, useRef } from 'react'
import { track } from '@/features/analytics'
import { LendBorrowActionType } from '@/features/analytics/types'

export const AssetInputSchema = z.object({
  symbol: z.string().transform(TokenSymbol),
  value: z.string().refine(
    (data) => {
      const value = Number.parseFloat(data)
      return data === '' || !Number.isNaN(value)
    },
    {
      message: 'Value must be a valid number',
    },
  ),
  isMaxSelected: z.boolean().default(false),
})
export type AssetInputSchema = z.input<typeof AssetInputSchema>

export interface DialogFormNormalizedData {
  position: UserPosition
  reserve: Reserve
  token: Token
  value: NormalizedUnitNumber
  isMaxSelected: boolean
}

export function normalizeDialogFormValues(asset: AssetInputSchema, marketInfo: MarketInfo): DialogFormNormalizedData {
  const canonicalSymbol = getCanonicalSymbol(marketInfo.chainId, TokenSymbol(asset.symbol))
  const token = marketInfo.findOneTokenBySymbol(canonicalSymbol)
  const position = marketInfo.findOnePositionBySymbol(canonicalSymbol)
  const value = NormalizedUnitNumber(asset.value === '' ? '0' : asset.value)

  return {
    position,
    reserve: position.reserve,
    token,
    value,
    isMaxSelected: asset.isMaxSelected ?? false,
  }
}

export function isMaxValue(value: string, maxValue: NormalizedUnitNumber): boolean {
  const normalizedValue = NormalizedUnitNumber(value === '' ? '0' : value)
  return normalizedValue.eq(maxValue)
}

function getNormalizedDialogFormValuesKey(values: DialogFormNormalizedData): string {
  return [values.token.address, values.value.toFixed(), values.isMaxSelected].join('-')
}

export interface UseDebouncedDialogFormValuesArgs {
  form: UseFormReturn<AssetInputSchema>
  marketInfo: MarketInfo
  capValue?: NormalizedUnitNumber
  actionType?: LendBorrowActionType
}
export interface UseDebouncedDialogFormValuesResult {
  debouncedFormValues: DialogFormNormalizedData
  isFormValid: boolean
  isDebouncing: boolean
}
export function useDebouncedDialogFormValues({
  form,
  marketInfo,
  actionType,
}: UseDebouncedDialogFormValuesArgs): UseDebouncedDialogFormValuesResult {
  const formValues = normalizeDialogFormValues(form.watch(), marketInfo)
  const isFormValid = form.formState.isValid
  const { debouncedValue, isDebouncing } = useDebounce(
    { formValues, isFormValid },
    getNormalizedDialogFormValuesKey(formValues) + isFormValid.toString(),
  )

  const lastAmountKeyRef = useRef<string>('')

  useEffect(() => {
    const { formValues: debouncedFormValues } = debouncedValue
    if (!debouncedFormValues || debouncedFormValues.value.eq(0)) return

    const key = `${debouncedFormValues.token.symbol}_${debouncedFormValues.value.toFixed()}`
    if (key === lastAmountKeyRef.current) return
    lastAmountKeyRef.current = key

    track('lend_borrow_amount_entered', {
      token: debouncedFormValues.token.symbol,
      market: debouncedFormValues.token.address,
      market_type: 'standard',
      action_type: actionType ?? 'deposit',
      amount: debouncedFormValues.value.toFixed(),
    })
  }, [debouncedValue, actionType])

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}
